import { createRoot } from "react-dom/client";
import { logger } from '@/lib/logger';
import App from "./App.tsx";
import "./index.css";
import { ViewportSimulatorProvider } from "./contexts/ViewportSimulatorContext";
import { saveResumeState, consumeResumeState, isStandalonePWA } from "@/lib/pwaResumeState";
import { supabase } from "./integrations/supabase/client";
import { checkAndForceUpdate, CURRENT_BUILD_ID } from "./lib/buildVersion";

// ── Build version guard: kick off ASAP so a stale bundle self-corrects
// before it has a chance to render any UI. Runs in parallel with React boot;
// if a mismatch is detected, the page hard-reloads via location.replace().
logger.log(`[ForjaHub] build=${CURRENT_BUILD_ID}`);
checkAndForceUpdate().catch(() => {});

// ── PWA install prompt: capture BEFORE React mounts ──
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let _deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let _appMounted = false;

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  _deferredInstallPrompt = e as BeforeInstallPromptEvent;
});

(window as any).__pwaInstallPrompt = {
  get: (): BeforeInstallPromptEvent | null => _deferredInstallPrompt,
  clear: () => { _deferredInstallPrompt = null; },
};

window.addEventListener('appinstalled', () => {
  localStorage.setItem('pwa-icon-version-acked', '2');
  import('@/services/pwaInstalls').then(m => m.trackPwaInstall());
});

// ── Service Worker registration & lifecycle ──
let _swReloading = false; // guard against double-reload

if ('serviceWorker' in navigator) {
  (async () => {
    try {
      // Unregister legacy workers
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        const scriptURL = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '';
        if (!scriptURL.includes('/push-sw.js')) {
          await reg.unregister();
        }
      }

      // Register push service worker
      const reg = await navigator.serviceWorker.register('/push-sw.js', {
        updateViaCache: 'none',
      });

      // Force an immediate update check on every boot. Without this, browsers
      // may keep serving the previously installed SW for hours/days. Combined
      // with the build-time version stamp in push-sw.js, this guarantees that
      // newly published bundles reach already-installed PWAs on next launch.
      reg.update().catch(() => {});

      // When a new SW is found, nudge it to activate
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              logger.log('[SW] New version installed, will activate on claim');
            }
          });
        }
      });

      // ── Visibility change: session refresh + SW update check ──
      let lastVisibleAt = Date.now();
      const BACKGROUND_UPDATE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
      const SESSION_REFRESH_THRESHOLD_MS = 15 * 60 * 1000;  // 15 minutes

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          const elapsed = Date.now() - lastVisibleAt;
          lastVisibleAt = Date.now();

          // Refresh auth session only if the tab was in background long
          // enough that the JWT might be near expiration. Calling getSession
          // on every quick tab-switch can cascade into TOKEN_REFRESHED storms
          // that re-render the whole tree and look like a page reload.
          if (elapsed > SESSION_REFRESH_THRESHOLD_MS) {
            supabase.auth.getSession().catch(() => {});
          }

          // Only check for new builds / SW updates if the tab was in the
          // background for at least 1 hour. Quick tab-switches (Cmd+Tab,
          // checking a notification, etc.) must NOT trigger reloads — that
          // ruins UX (lost scroll position, in-progress forms, video state).
          // The boot check at the top of this file already covers fresh
          // app opens, and the periodic interval below covers tabs left
          // open for hours.
          if (elapsed > BACKGROUND_UPDATE_THRESHOLD_MS) {
            reg.update().catch(() => {});
            checkAndForceUpdate().catch(() => {});
          }
        } else {
          lastVisibleAt = Date.now();
          // Save current route before app goes to background
          saveResumeState();
        }
      });

      // ── Periodic safety net: for users who keep the tab open all day ──
      // Check for a new published build once an hour while the tab is visible.
      // If a deploy happened, this will trigger a guarded reload via
      // checkAndForceUpdate(); otherwise it's a cheap no-op fetch.
      setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        reg.update().catch(() => {});
        checkAndForceUpdate().catch(() => {});
      }, BACKGROUND_UPDATE_THRESHOLD_MS);

    } catch (err) {
      console.error('[SW] Registration failed:', err);
    }
  })();

  // ── Reload-loop guard ──
  const canAutoReload = (): boolean => {
    const now = Date.now();
    const count = parseInt(sessionStorage.getItem('pwa-reload-count') || '0', 10);
    const ts = parseInt(sessionStorage.getItem('pwa-reload-ts') || '0', 10);
    if (now - ts > 30_000) {
      sessionStorage.setItem('pwa-reload-count', '1');
      sessionStorage.setItem('pwa-reload-ts', String(now));
      return true;
    }
    if (count >= 2) return false;
    sessionStorage.setItem('pwa-reload-count', String(count + 1));
    return true;
  };

  const guardedReload = () => {
    if (!_appMounted) return; // Don't reload before React mounts
    if (_swReloading) return;
    if (!canAutoReload()) {
      console.warn('[PWA] Reload loop detected — skipping auto-reload');
      return;
    }
    _swReloading = true;
    saveResumeState();
    window.location.reload();
  };

  // ── controllerchange: intentionally NOT auto-reloading ──
  // Reloading on controllerchange used to wipe open forms (e.g. the admin
  // "Nova notificação" modal) every time the user came back to the tab.
  // Build freshness is already covered by checkAndForceUpdate() at boot
  // and on long backgrounds (>1h) in the visibilitychange handler above.

  // ── SW message: only act on explicit FORCE_REFRESH (admin push). ──
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'SW_UPDATED' && event.data?.forced === true) {
      guardedReload();
    }
  });
}

// ── PWA Resume: restore route after reload/background eviction ──
const resumeUrl = consumeResumeState();
if (resumeUrl && window.location.pathname === '/') {
  history.replaceState(null, '', resumeUrl);
}

// ── Bootstrap with nuclear recovery on failure ──
function nuclearRecovery() {
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;height:100vh;background:#0a0b0d">' +
      '<div style="text-align:center;padding:24px">' +
      '<p style="color:#f97316;font-size:18px;font-weight:600;margin:0 0 8px;font-family:system-ui,sans-serif">ForjaHub</p>' +
      '<p style="color:rgba(255,255,255,.6);font-size:14px;margin:0 0 20px;font-family:system-ui,sans-serif">Erro ao carregar. Vamos limpar e tentar de novo.</p>' +
      '<button id="nuclear-btn" style="padding:10px 28px;background:#f97316;border:none;border-radius:8px;color:#fff;font-size:14px;cursor:pointer;font-family:system-ui,sans-serif;font-weight:600">Recarregar</button>' +
      '</div></div>';
    document.getElementById('nuclear-btn')?.addEventListener('click', async () => {
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        }
        if (caches) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k)));
        }
      } catch (_) {}
      window.location.reload();
    });
  }
}

try {
  createRoot(document.getElementById("root")!).render(
    <ViewportSimulatorProvider>
      <App />
    </ViewportSimulatorProvider>
  );
  _appMounted = true;
} catch (err) {
  console.error('[PWA] Bootstrap failed:', err);
  nuclearRecovery();
}
